import { defaultHandlePlayerClick } from '@sportal365/smp-widget-utils';
import { Property } from '@stitches/react/types/css';
import { ConfigContext, SMPContainer, SMPText } from '@velinsi/smp-widgets-ui';
import { FC, useContext } from 'react';
import { formatPlayerName } from '../../utils/nameFormatters';
import { NameFormat } from '../../../../../packages/types/football';
import { PLAYER_ENTITY } from '../../../../../packages/constants/entityTypes';
import { GenericLayout, GenericSize } from '../../../../../packages/constants/generic';
import { styled } from '../../../../../packages/theme/stitches.config';
import { IMatchPlayerLineup, ISubscriberInfo } from '../../../../../packages/types';
import LinkServiceContext from '../../contexts/LinkServiceContext';
import { ThumbComponent } from './Thumb';

export const THUMB_SIZE_BREAKPOINT_3 = 772; // Width below which player thumb sizes are smaller

const getMaxWidth = ({
  alignmentInContainer,
  fieldWidth,
  numberOfPlayersWithSamePosition,
  numberOfPositions,
}: Pick<LineupsPlayerProps, 'alignmentInContainer' | 'fieldWidth' | 'numberOfPlayersWithSamePosition' | 'numberOfPositions'>) => {
  let maxWidth: number;

  if (alignmentInContainer === GenericLayout.HORIZONTAL) {
    maxWidth = fieldWidth / 2 / numberOfPositions - 4;
  } else {
    maxWidth = Math.min(fieldWidth / numberOfPlayersWithSamePosition, 250);
  }

  return maxWidth;
};

interface LineupsPlayerProps {
  playerLineup: IMatchPlayerLineup;
  numberOfPlayersWithSamePosition: number;
  numberOfPositions: number;
  alignmentInContainer: GenericLayout.HORIZONTAL | GenericLayout.VERTICAL;
  fieldWidth: number;
  borderColor: Property.BorderColor;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
}

const LineupsPlayer: FC<LineupsPlayerProps> = ({
  playerLineup,
  numberOfPlayersWithSamePosition,
  numberOfPositions,
  alignmentInContainer,
  fieldWidth,
  borderColor,
  handleClick,
}) => {
  const { dataNameFormat } = useContext(ConfigContext);
  const { linkService, urlTarget } = useContext(LinkServiceContext);

  const size = alignmentInContainer === GenericLayout.HORIZONTAL ? GenericSize.LARGE : GenericSize.SMALL;

  const shirtNumber = playerLineup.shirtNumber?.toString() ?? '';
  const events = playerLineup.events;
  const thumbUrl = playerLineup.player.assets.thumb_transparent_background?.url;
  const displayPlayerName = formatPlayerName(playerLineup.player.name, dataNameFormat as NameFormat);

  const maxWidth = getMaxWidth({
    alignmentInContainer,
    fieldWidth,
    numberOfPlayersWithSamePosition,
    numberOfPositions,
  });

  const handlePlayerClick = () => defaultHandlePlayerClick(handleClick, playerLineup.player.id, displayPlayerName);
  const isHorizontal = alignmentInContainer === GenericLayout.HORIZONTAL;

  return (
    <PlayerWrapper style={{ width: `${maxWidth}px` }}>
      <SMPContainer css={{ padding: 0, '&:hover': { zIndex: '$max' } }} onClick={handlePlayerClick}>
        <PlayerContainer
          href={linkService?.createEntityUrl(String(playerLineup.player.id), PLAYER_ENTITY, '', playerLineup.player.slug) ?? '#'}
          target={urlTarget}
        >
          <ThumbComponent imageUrl={thumbUrl} borderColor={borderColor} size={size} events={events} context='field' />
        </PlayerContainer>
      </SMPContainer>
      <TextContainer>
        <SMPContainer
          style={{
            borderRadius: '2px',
            padding: isHorizontal ? '4px 2px 4px 0px' : '2px 2px 2px 0px',
            maxWidth: '100%',
            textAlign: 'center',
            wordBreak: 'normal',
            pointerEvents: 'all',
            fontSize: isHorizontal ? '12px' : '10px',
            height: isHorizontal ? '18px' : '14px',
            lineHeight: isHorizontal ? '12px' : '10px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.5))',
            display: 'inline',
          }}
          variant='sportEntityContainer'
        >
          <ShirtNumber>{shirtNumber}</ShirtNumber>
          <SMPText variant='contrast' css={{ display: 'inline' }}>
            {'\u00A0'}
            <span style={{ fontWeight: 600 }}>{displayPlayerName}</span>
          </SMPText>
        </SMPContainer>
      </TextContainer>
    </PlayerWrapper>
  );
};

const PlayerWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const PlayerContainer = styled('a', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  outline: 'none',
  textDecoration: 'none',
  maxWidth: '100%',
});

const TextContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'center',
  width: '100%',
  pointerEvents: 'none',
  marginTop: '2px',
});

const ShirtNumber = styled('span', {
  backgroundColor: '$gray6',
  color: 'white',
  padding: '3px',
});

export default LineupsPlayer;
